import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { History } from 'lucide-react';
import { operationsApi } from '@/services/api';
import type { OperationsChecklist } from '@/types/operations';
import { PageFrame, PageHeader, Surface, Button, Note, Placeholder } from '@/components/ui';
import shared from '@/components/utils/shared.module.css';
import ChecklistView from './ChecklistView';
import { getApiErrorMessage } from './helpers';

const OperationsChecklistPage: React.FC = () => {
  const [checklist, setChecklist] = useState<OperationsChecklist | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await operationsApi.getCurrent();
      setChecklist(data);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to load the operations checklist.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Operations Checklist"
          subtitle="Today's operational steps, and whether each one has passed."
          actions={
            /* A link, not a button — it navigates. Styled from the shared
               button class so it sits level with the kit's buttons. */
            <Link to="/operations-checklist/history" className={shared['btn-secondary']}>
              <History size={15} /> History
            </Link>
          }
        />
      }
    >
      {loading && (
        <Surface>
          <Placeholder kind="loading" message="Loading checklist…" />
        </Surface>
      )}

      {!loading && error && (
        <Note tone="error">
          {error}{' '}
          <Button size="sm" onClick={load}>Retry</Button>
        </Note>
      )}

      {!loading && !error && !checklist && (
        <Surface>
          <Placeholder message="No checklist found for today." />
        </Surface>
      )}

      {!loading && !error && checklist && (
        checklist.steps.length === 0
          ? (
            <Surface>
              <Placeholder message="No active steps for this checklist." />
            </Surface>
          )
          : <ChecklistView checklist={checklist} onChange={setChecklist} />
      )}
    </PageFrame>
  );
};

export default OperationsChecklistPage;
